import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, Card } from '@mui/material';
import RealtorForm from '../common/forms/RealtorForm';
import { IRealtorInfo } from '../../types/CreateDealForm';
import { FlexBetween } from '../../commonStyles';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { formatPhoneNumber } from '../../utils/Functions';

export const PHONE_FIELDS = ['brokeragePhone', 'directPhone', 'brokerageFax'];

export const REQUIRED_FIELDS: (keyof IRealtorInfo)[] = [
  'firstName',
  'lastName',
  'brokerage',
  'email',
  'streetAddress',
  'city',
  'province',
  'country',
  'postalCode',
  'brokeragePhone',
  'directPhone',
];

// The selected realtor's information, which can be edited and saved back to the realtor
const RealtorDetails = (props: ChildProps) => {
  const { realtor, onUpdated, editing, setEditing } = props;
  const storeDispatch = useAppDispatch();
  const [draft, setDraft] = useState<IRealtorInfo>(realtor);

  const [updateRealtor, { loading }] = useMutation(UPDATEREALTOR, {
    onCompleted: (data) => {
      onUpdated(data.realtorUpdateById.record);
      setEditing(false);
      storeDispatch(showSuccessSnackbar('Realtor Information has been updated!'));
    },
    onError: (error) => {
      storeDispatch(showErrorSnackbar(error.message));
    },
  });

  const handleEdit = () => {
    setDraft(realtor);
    setEditing(true);
  };

  const handleTextInput = (event: any) => {
    const { id, value } = event.target;
    setDraft({ ...draft, [id]: PHONE_FIELDS.includes(id) ? formatPhoneNumber(value) : value });
  };

  const handleUpdate = () => {
    if (REQUIRED_FIELDS.some((field) => !draft[field])) {
      storeDispatch(showErrorSnackbar('Please complete all required realtor fields'));
      return;
    }

    updateRealtor({
      variables: {
        _id: realtor._id,
        record: {
          firstName: draft.firstName,
          lastName: draft.lastName,
          brokerage: draft.brokerage,
          email: draft.email,
          streetAddress: draft.streetAddress,
          city: draft.city,
          province: draft.province,
          country: draft.country,
          postalCode: draft.postalCode,
          brokeragePhone: draft.brokeragePhone,
          brokerageFax: draft.brokerageFax,
          directPhone: draft.directPhone,
        },
      },
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      {editing ? (
        <Box>
          <RealtorForm realtorInfo={draft} handleTextInput={handleTextInput} />
          <FlexBetween sx={{ mt: 2 }}>
            <Button color="success" variant="contained" disabled={loading} onClick={handleUpdate}>
              Update Realtor
            </Button>
            <Button variant="contained" disabled={loading} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </FlexBetween>
        </Box>
      ) : (
        <Card sx={{ border: '1px solid #000', p: 2, fontSize: '14px', maxWidth: 600 }}>
          <Box>
            Realtor Name:{' '}
            <strong>
              {realtor.firstName} {realtor.lastName}
            </strong>
          </Box>
          <Box sx={{ mt: 1 }}>
            Brokerage: <strong>{realtor.brokerage}</strong>
          </Box>
          <Box sx={{ mt: 1 }}>
            Brokerage Address:{' '}
            <strong>
              {realtor.streetAddress} {realtor.city} {realtor.province} {realtor.country} {realtor.postalCode}
            </strong>
          </Box>
          <Box sx={{ mt: 1 }}>
            Realtor Email: <strong>{realtor.email}</strong>
          </Box>
          <Box sx={{ mt: 1 }}>
            Realtor Phone Number: <strong>{realtor.directPhone}</strong>
          </Box>
          <Box sx={{ mt: 1 }}>
            Brokerage Phone Number: <strong>{realtor.brokeragePhone}</strong>
          </Box>
          {realtor.brokerageFax ? (
            <Box sx={{ mt: 1 }}>
              Brokerage Fax: <strong>{realtor.brokerageFax}</strong>
            </Box>
          ) : null}
          <Button sx={{ mt: 2 }} variant="contained" color="primary" onClick={handleEdit}>
            Edit
          </Button>
        </Card>
      )}
    </Box>
  );
};

interface ChildProps {
  realtor: IRealtorInfo;
  onUpdated: (realtor: IRealtorInfo) => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
}

const UPDATEREALTOR = gql`
  mutation realtorUpdateById($_id: MongoID!, $record: UpdateByIdRealtorInput!) {
    realtorUpdateById(_id: $_id, record: $record) {
      record {
        _id
        email
        firstName
        lastName
        fullName
        brokerage
        streetAddress
        city
        province
        country
        postalCode
        brokeragePhone
        brokerageFax
        directPhone
        disable
      }
    }
  }
`;

export default RealtorDetails;
